import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import {
  AppCard,
  AppCheckbox,
  AppFileUploader,
  AppInput,
  AppModal,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Sidebar from '../../../layout/Sidebar/Sidebar';
import AppLink from '../../../routing/helpers/AppLink';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import { axiosPath, path } from '../../../routing/helpers/router';
import minions from '../../../minions/minions';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import colors from '../../../constants/colors';
import errorHandler from '@/layout/components/error/ErrorHandler';

export default function UserQueryListScreen() {
  const { moduleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(null);
  const [counter, setCounter] = useState(5);
  const [layout, setLayout] = useState(null);
  const [userQueryTheads, setUserQueryTheads] = useState(null);
  const [showAddBanner, setShowAddBanner] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const client = useSelector((state) => state?.auth?.user?.client);
  const userStore = useSelector((state) => state?.auth?.user);
  const validationSchema = Yup.object().shape({
    subject: Yup.string().required(
      ReactDOMServer.renderToString(
        <Translator file="user" name="user.required" />,
      ),
    ),
    cc: Yup.string(),
    message: Yup.string(),
  });
  const [initialFilters] = useState({
    text: '',
    showOnlyMe: true,
    state: 0,
  });
  const options = [
    ...(plantHex != 1
      ? [
          {
            value: 0,
            label: ReactDOMServer.renderToString(
              <Translator
                file="module-legalCompliance"
                name="m-gcl-inquiry.about.facility"
              />,
            ),
          },
        ]
      : []),

    {
      value: 1,
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-legalCompliance"
          name="m-gcl-consultation.on.module"
        />,
      ),
    },
    {
      value: 2,
      label: ReactDOMServer.renderToString(
        <Translator file="module-legalCompliance" name="m-gcl-private" />,
      ),
    },
  ];

  // > LOAD USERREQUEST
  const loadUserQuery = useCallback(
    async (filters) => {
      await axios
        .post(axiosPath('api_r_user_userQuery_list'), {
          moduleBridgeHex,
          plantHex,
          filters,
        })
        .then((r) => {
          if (isMounted.current) {
            setLayout(r.data.layout);
            setUserQueryTheads(r.data.userQueryList || []);
          }
        })
        .catch((err) => {
          if (err.response.status === 428)
            navigate(
              path({
                name: 'user_home',
              }),
            );
        });
    },
    [moduleBridgeHex, plantHex],
  );

  useEffect(() => {
    loadUserQuery({ ...initialFilters });
  }, [moduleBridgeHex, plantHex]);
  // < LOAD USERREQUEST

  const handleOnSubmit = async (values) => {
    if (isLoading) return;
    setIsLoading(true);
    await axios
      .post(
        axiosPath('api_r_user_userQuery_add'),
        minions.forms.toFormData({ ...values, moduleBridgeHex, plantHex }),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((response) => {
        setUserQueryTheads(undefined);
        loadUserQuery({ ...initialFilters });
        setShowAddModal(false);
        AppNotificationAlert({
          title: 'OK',
          message: response.data.message,
          type: response.data.type,
          position: 'top-right',
          duration: 3000,
        });
      })
      .catch((err) => {
        dispatch(errorHandler(err));
      })
      .finally(() => setIsLoading(false));
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
    setShowAddBanner(true);
  };

  useEffect(() => {
    if (showAddBanner) {
      if (counter === 0) return;

      const intervalo = setInterval(() => {
        setCounter((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(intervalo);
    }
  }, [showAddBanner]);

  const getTypeIcon = (type) => {
    switch (type) {
      case 1:
        return {
          icon: 'earth',
          color: colors.success,
          text: ReactDOMServer.renderToString(
            <Translator
              file="module-legalCompliance"
              name="m-gcl-consultation.on.module"
            />,
          ),
        };
      case 2:
        return {
          icon: 'eye-off',
          color: colors.gray700,
          text: ReactDOMServer.renderToString(
            <Translator file="module-legalCompliance" name="m-gcl-private" />,
          ),
        };
      default:
        return {
          icon: 'building-4',
          color: colors.danger,
          text: ReactDOMServer.renderToString(
            <Translator
              file="module-legalCompliance"
              name="m-gcl-inquiry.about.this.facility"
            />,
          ),
        };
    }
  };

  return (
    <>
      <Sidebar>
        <img
          src={client?.logoUrl}
          className="img-fluid"
          alt={client?.name}
          title={client?.name}
        />
        <Accordion>
          <ul>
            <li>
              <AppLink
                path="user_module_legalCompliance_home"
                params={{ clientID: client?.id, moduleBridgeHex }}
                className="sidebar-element"
              >
                <AppRemixIcon
                  icon="arrow-go-back-line"
                  className="sidebar-icon"
                  noVariants
                />
                <span className="sb-title">
                  <Translator file="module-legalCompliance" name="go.back" />
                </span>
              </AppLink>
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  clientID: client?.id,
                  moduleBridgeHex,
                  plantHex,
                  page: 1,
                  filtersHex: 0,
                }}
                className="sidebar-element"
              >
                <AppRemixIcon icon="check-double" className="sidebar-icon" />{' '}
                <span className="sb-title">
                  <Translator
                    file="module-legalCompliance"
                    name="go.to.evaluation"
                  />
                </span>
              </AppLink>
            </li>
          </ul>
        </Accordion>
      </Sidebar>

      <BreadcrumbHandler
        paths={
          layout
            ? plantHex != 1
              ? [
                  {
                    name: layout?.plant?.name,
                    route: {
                      name: 'user_module_legalCompliance_evaluation',
                      params: {
                        moduleBridgeHex,
                        plantHex,
                        page: 1,
                        filtersHex: 0,
                      },
                    },
                  },
                  {
                    name: ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-consult"
                      />,
                    ),
                  },
                ]
              : [
                  {
                    name: ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-consult"
                      />,
                    ),
                  },
                ]
            : [
                {
                  name: '...',
                },
              ]
        }
      />

      <div
        className="btn btn-sm btn-outline-primary mb-4 ml-4"
        onClick={() => handleShowAddModal()}
      >
        <Translator file="module-legalCompliance" name="m-gcl-new.inquiry" />
      </div>
      <AppCard className="ml-4 mr-4">
        <div className="mb-3">
          <Formik
            initialValues={initialFilters}
            // validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={(v) => loadUserQuery(v)}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppInput
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="m-gcl-filter.text.subject.conversation"
                    />,
                  )}
                  placeholder={ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="m-gcl-enter.title"
                    />,
                  )}
                  icon={<AppRemixIcon icon="font-color" noVariants />}
                  onChange={(value) => setFieldValue('text', value)}
                  value={values.text}
                  isClearable
                  error={errors.text}
                  onPressKey={(value) => handleSubmit(value)}
                  onPressKeyList={['Enter']}
                />
                <div className="row">
                  <div className="col-6">
                    <AppSingleSelect
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="m-gcl-status"
                        />,
                      )}
                      icon={<AppRemixIcon icon="issues-reopen" />}
                      value={values.state}
                      options={[
                        {
                          label: ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="m-gcl-all"
                            />,
                          ),
                          value: 0,
                        },
                        {
                          label: ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="m-gcl-awaiting.response"
                            />,
                          ),
                          value: 1,
                        },
                        {
                          label: ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="m-gcl-managed"
                            />,
                          ),
                          value: 2,
                        },
                      ]}
                      onChange={(v) => setFieldValue('state', v)}
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <AppCheckbox
                    value={values.showOnlyMe}
                    onChange={(opt) => setFieldValue('showOnlyMe', !!opt)}
                  >
                    <Translator
                      file="module-legalCompliance"
                      name="m-gcl-show.own"
                    />
                  </AppCheckbox>

                  <button
                    type="submit"
                    className="btn btn-sm btn-outline-primary"
                  >
                    <Translator
                      file="module-legalCompliance"
                      name="m-gcl-filter"
                    />
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>

        {userQueryTheads ? (
          userQueryTheads.length > 0 ? (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col" style={{ width: '100px' }}></th>
                    <th scope="col">Titulo de consulta</th>
                    <th scope="col" style={{ width: '350px' }}>
                      <Translator
                        file="module-legalCompliance"
                        name="gcl-information"
                      />
                    </th>
                    <th scope="col" className="text-center">
                      <Translator file="module-legalCompliance" name="type" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {userQueryTheads?.map((userQueryThead) => (
                    <tr
                      key={userQueryThead.id}
                      className={
                        userQueryThead.isRevised
                          ? ''
                          : userStore.name === userQueryThead.user
                            ? 'table-warning'
                            : ''
                      }
                      title={
                        userQueryThead.isRevised
                          ? ''
                          : userStore.name === userQueryThead.user
                            ? ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="m-gcl-you.message.query"
                                />,
                              )
                            : ''
                      }
                    >
                      <td>
                        <AppLink
                          path="user_module_legalCompliance_userQuery_messages"
                          params={{
                            moduleBridgeHex,
                            plantHex,
                            userQueryThreadHEX: userQueryThead?.hex,
                          }}
                          className="btn btn-sm btn-outline-success"
                        >
                          <AppRemixIcon icon="chat-1" />
                        </AppLink>
                      </td>
                      <td>
                        <div>
                          <strong>{userQueryThead?.subject}</strong>
                        </div>
                        <div>{userQueryThead?.message}</div>
                      </td>
                      <td>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>
                              <Translator
                                file="module-legalCompliance"
                                name="m-gcl-status"
                              />
                              , :
                            </b>
                          </div>
                          <div
                            className={
                              userQueryThead.state ? 'text-success' : ''
                            }
                          >
                            {userQueryThead.state ? (
                              <Translator
                                file="module-legalCompliance"
                                name="m-gcl-managed.consultation"
                              />
                            ) : (
                              <Translator
                                file="module-legalCompliance"
                                name="m-gcl-awaiting.response"
                              />
                            )}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>CODE:</b>
                          </div>
                          <div> {userQueryThead.hex}</div>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>
                              <Translator
                                file="module-legalCompliance"
                                name="wl-gcl-created"
                              />
                              :
                            </b>
                          </div>
                          <div> {userQueryThead?.dateStamp}</div>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>
                              <Translator
                                file="module-legalCompliance"
                                name="m-gcl-last.update"
                              />
                              :
                            </b>
                          </div>
                          <div>{userQueryThead?.dateLastMessage}</div>
                        </div>
                      </td>

                      <td className="text-center">
                        {(() => {
                          const { icon, color, text } = getTypeIcon(
                            userQueryThead.type,
                          );
                          return (
                            <AppRemixIcon
                              icon={icon}
                              size="20"
                              color={color}
                              title={text}
                            />
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <AppNoInfo
              icon={<AppRemixIcon icon="notification" color="#ccc" size={34} />}
              text={ReactDOMServer.renderToString(
                <Translator
                  file="module-legalCompliance"
                  name="m-gcl-there.no.active.queries"
                />,
              )}
            />
          )
        ) : (
          <AppNoInfo>
            <SpinnerLoader />
          </AppNoInfo>
        )}
      </AppCard>
      <AppModal
        title={ReactDOMServer.renderToString(
          <Translator file="module-legalCompliance" name="m-gcl-new.inquiry" />,
        )}
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        size="xl"
      >
        <Formik
          initialValues={{
            type: plantHex == 1 ? 1 : 0,
            subject: '',
            cc: '',
            message: '',
            file: null,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleOnSubmit}
        >
          {({ handleSubmit, values, setFieldValue, errors }) => {
            if (showAddBanner) {
              return (
                <h5 className="p-2 text-center">
                  <Translator
                    file="module-legalCompliance"
                    name="m-gcl-remember.check.queries.created.resolved.questions"
                  />
                  <div
                    style={{
                      fontSize: '2rem',
                      textAlign: 'center',
                      padding: '1rem',
                    }}
                  >
                    <div style={{ height: '50px' }}>
                      {counter > 0 && counter}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        counter > 0 ? undefined : setShowAddBanner(false);
                      }}
                      className={`btn btn-sm btn-outline-${
                        counter > 0 ? 'light text-light' : 'primary'
                      }`}
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-accept"
                      />
                    </button>
                  </div>
                </h5>
              );
            }
            return (
              <>
                <div className="row">
                  <div className="col-lg-12">
                    <AppSingleSelect
                      icon={<AppRemixIcon icon="carousel-view" noVariants />}
                      options={options}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="m-gcl-type.inquiry"
                        />,
                      )}
                      value={values?.type}
                      onChange={(opt) => setFieldValue('type', opt)}
                      isClearable
                    />
                    <AppInput
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="m-gcl-title.query"
                        />,
                      )}
                      icon={<AppRemixIcon icon="double-quotes-l" noVariants />}
                      value={values.subject}
                      onChange={(opt) => setFieldValue('subject', opt)}
                      error={errors.subject}
                      isClearable
                    />
                  </div>
                  <div className="col-lg-12">
                    <AppInput
                      title="CC"
                      icon={<AppRemixIcon icon="share-forward" />}
                      value={values.cc}
                      onChange={(opt) => setFieldValue('cc', opt)}
                      error={errors.cc}
                      isClearable
                    />
                  </div>
                  <div className="col-lg-12">
                    <AppTextArea
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="m-gcl-details.consultation"
                        />,
                      )}
                      icon={<AppRemixIcon icon="mail" />}
                      onChange={(opt) => setFieldValue('message', opt)}
                      value={values.message}
                      error={errors.message}
                    />
                  </div>
                  <div className="col-lg-12">
                    <AppFileUploader
                      placeholder={
                        values.file ? (
                          values.file[0].name
                        ) : (
                          <Translator file="general" name="select.a.file" />
                        )
                      }
                      onChange={(opt) => setFieldValue('file', opt)}
                      btnText={<Translator file="general" name="search..." />}
                      isMulti
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-12 mt-4">
                    <span
                      className="btn btn-outline-light"
                      onClick={() => setShowAddModal(false)}
                    >
                      <Translator file="user" name="user.cancel" />
                    </span>
                    <span
                      className={`btn btn-success float-right ${
                        isLoading ? 'disabled' : ''
                      }`}
                      onClick={handleSubmit}
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.plant-info.request.send"
                      />
                    </span>
                  </div>
                </div>
              </>
            );
          }}
        </Formik>
      </AppModal>
    </>
  );
}
